import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X,
  IndianRupee,
  Users,
  Heart,
  Loader
} from 'lucide-react';
import { 
  getCaregiverServices, 
  addService, 
  updateService, 
  deleteService,
  toggleServiceAvailability 
} from '../services/servicesService';
import { SERVICE_CATALOG, toServiceSlug } from '../config/services';

const MyServices = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditing, setIsEditing] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Must mirror the catalog owners search by, otherwise a tagged service will
  // never surface in Find Care results.
  const serviceTypes = SERVICE_CATALOG.map(({ label, icon }) => ({ name: label, icon }));

  // Load services on mount
  useEffect(() => {
    loadServices();
  }, [user]);

  const loadServices = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userId = user.uid || user.id;
      const result = await getCaregiverServices(userId);
      
      if (result.success) {
        // Map icon names back to icon components
        const servicesWithIcons = result.services.map(service => ({
          ...service,
          icon: serviceTypes.find(type => type.name === service.name)?.icon || Heart
        }));
        setServices(servicesWithIcons);
      }
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setIsEditing(service.id);
    setEditForm(service);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateService(editForm.id, {
        name: editForm.name,
        slug: toServiceSlug(editForm.name),
        description: editForm.description,
        rate: editForm.rate,
        rateType: editForm.rateType,
        maxPets: editForm.maxPets
      });
      
      if (result.success) {
        setServices(services.map(service => 
          service.id === editForm.id ? editForm : service
        ));
        setIsEditing(null);
        setEditForm({});
      }
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(null);
    setEditForm({});
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const result = await deleteService(serviceId);
      
      if (result.success) {
        setServices(services.filter(service => service.id !== serviceId));
        toast.success('Service deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service. Please try again.');
    }
  };

  const handleAddService = async () => {
    setSaving(true);
    try {
      const userId = user.uid || user.id;
      const newService = {
        name: editForm.name,
        slug: toServiceSlug(editForm.name),
        description: editForm.description,
        rate: editForm.rate,
        rateType: editForm.rateType,
        maxPets: editForm.maxPets,
        available: true
      };
      
      console.log('Adding service:', newService);
      const result = await addService(userId, newService);
      console.log('Add service result:', result);
      
      if (result.success) {
        const serviceWithIcon = {
          ...result.service,
          icon: serviceTypes.find(type => type.name === editForm.name)?.icon || Heart
        };
        setServices([...services, serviceWithIcon]);
        setShowAddForm(false);
        setEditForm({});
        toast.success('Service added successfully!');
        // Reload services from database to ensure sync
        await loadServices();
      } else {
        console.error('Failed to add service:', result.error);
        toast.error(`Failed to add service: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(`Error: ${error.message || 'Failed to add service. Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (!service) return;
    
    try {
      const result = await toggleServiceAvailability(serviceId, !service.available);
      
      if (result.success) {
        setServices(services.map(service => 
          service.id === serviceId 
            ? { ...service, available: !service.available }
            : service
        ));
        toast.success(`Service ${!service.available ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast.error('Failed to update availability. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="pf-page-wrap pb-32 lg:pb-20 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[var(--pf-accent)] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/caregiver-dashboard" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Services</h1>
              <p className="text-gray-600 mt-2">Manage your pet care services and pricing</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              disabled={saving}
              className="pf-btn-primary px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add Service
            </button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const Icon = service.icon;
            const isCurrentlyEditing = isEditing === service.id;

            return (
              <div key={service.id} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                {isCurrentlyEditing ? (
                  // Edit Form
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Service name"
                    />
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      className="w-full p-2 border rounded-lg h-20"
                      placeholder="Service description"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        value={editForm.rate}
                        onChange={(e) => setEditForm({...editForm, rate: parseInt(e.target.value)})}
                        className="p-2 border rounded-lg"
                        placeholder="Rate"
                      />
                      <select
                        value={editForm.rateType}
                        onChange={(e) => setEditForm({...editForm, rateType: e.target.value})}
                        className="p-2 border rounded-lg"
                      >
                        <option value="per day">per day</option>
                        <option value="per hour">per hour</option>
                        <option value="per walk">per walk</option>
                        <option value="per night">per night</option>
                      </select>
                    </div>
                    <input
                      type="number"
                      value={editForm.maxPets}
                      onChange={(e) => setEditForm({...editForm, maxPets: parseInt(e.target.value)})}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Max pets"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center gap-1 flex-1 disabled:opacity-50"
                      >
                        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={saving}
                        className="bg-gray-300 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-400 flex items-center gap-1 flex-1 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display Mode
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[var(--pf-accent-soft)] rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-[var(--pf-accent)]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{service.name}</h3>
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            service.available 
                              ? 'bg-green-100 text-green-600' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {service.available ? 'Available' : 'Unavailable'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(service)}
                          className="p-2 text-gray-400 hover:text-gray-600"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="p-2 text-gray-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <IndianRupee className="w-4 h-4" />
                        <span>₹{service.rate} {service.rateType}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>Up to {service.maxPets} pets</span>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleAvailability(service.id)}
                      className={`w-full mt-4 py-2 px-4 rounded-lg font-medium transition-colors ${
                        service.available
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {service.available ? 'Mark Unavailable' : 'Mark Available'}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Service Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold">Add New Service</h3>
                  {editForm.name && (
                    <p className="text-[var(--pf-accent)] font-semibold mt-1">{editForm.name}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditForm({});
                  }}
                  disabled={saving}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Type *</label>
                  <select
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className="w-full p-2 border rounded-lg"
                  >
                    <option value="">Select a service</option>
                    {serviceTypes.map((type) => (
                      <option key={type.name} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    value={editForm.description || ''}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    className="w-full p-2 border rounded-lg h-20"
                    placeholder="Describe your service..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate *</label>
                    <input
                      type="number"
                      value={editForm.rate || ''}
                      onChange={(e) => setEditForm({...editForm, rate: parseInt(e.target.value) || 0})}
                      className="w-full p-2 border rounded-lg"
                      placeholder="₹ Amount"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type</label>
                    <select
                      value={editForm.rateType || 'per day'}
                      onChange={(e) => setEditForm({...editForm, rateType: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="per day">per day</option>
                      <option value="per hour">per hour</option>
                      <option value="per walk">per walk</option>
                      <option value="per night">per night</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Maximum Pets *</label>
                  <input
                    type="number"
                    value={editForm.maxPets || ''}
                    onChange={(e) => setEditForm({...editForm, maxPets: parseInt(e.target.value) || 0})}
                    className="w-full p-2 border rounded-lg"
                    placeholder="How many pets can you handle?"
                    min="1"
                  />
                </div>
                <p className="text-xs text-gray-500">* Required fields</p>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditForm({});
                  }}
                  disabled={saving}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex-1 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddService}
                  className="pf-btn-primary px-4 py-2 rounded-lg flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
                  disabled={!editForm.name || !editForm.description || !editForm.rate || isNaN(editForm.rate) || editForm.rate <= 0 || !editForm.maxPets || isNaN(editForm.maxPets) || editForm.maxPets <= 0 || saving}
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Add Service
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyServices;
